import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/lib/utils';

/**
 * A short explanation that floats beside its trigger.
 *
 * Not a `title`: `TimeZoneFootnote` and the old locked menu rows avoided one
 * because a native tooltip is reachable neither by keyboard nor by touch. This
 * one opens on every input there is — pointer hover, keyboard focus, and a tap
 * or click — and closes on leave, blur, Escape or a press elsewhere.
 *
 * Built on `@radix-ui/react-popover` (already a dependency) for the portal,
 * positioning, collision handling and dismissal; `@radix-ui/react-tooltip` was
 * not added because it deliberately ignores touch, which is half the reason this
 * component exists. The popover is anchored, not triggered: Radix's trigger
 * toggles on click, which would close on the click that follows a hover-open.
 *
 * The text reaches assistive technology through `aria-describedby` on the
 * trigger, pointing at a visually hidden copy that is always in the DOM. The
 * floating copy is `aria-hidden`, so the explanation is announced once, with the
 * trigger, whether or not anyone has hovered it.
 */
export interface TooltipProps {
  /** What the floating panel shows. Spanish, short. */
  content: React.ReactNode;
  /**
   * The accessible description, when `content` is richer than what should be
   * read after the trigger's own name (e.g. it repeats the label). Defaults to
   * `content`.
   */
  description?: React.ReactNode;
  /** The trigger: ONE focusable element that accepts the handlers below. */
  children: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

export function Tooltip({
  content,
  description = content,
  children,
  side = 'top',
  className,
}: TooltipProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const descriptionId = React.useId();
  const own = children.props;

  const trigger = React.cloneElement(children, {
    'aria-describedby': cn(own['aria-describedby'], descriptionId),
    onPointerEnter: (event: React.PointerEvent<HTMLElement>) => {
      own.onPointerEnter?.(event);
      // A finger has no hover: on touch the tap below opens it, and a synthetic
      // pointerenter would make that tap look like a second toggle.
      if (event.pointerType === 'mouse') setOpen(true);
    },
    onPointerLeave: (event: React.PointerEvent<HTMLElement>) => {
      own.onPointerLeave?.(event);
      if (event.pointerType === 'mouse') setOpen(false);
    },
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      own.onFocus?.(event);
      setOpen(true);
    },
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      own.onBlur?.(event);
      setOpen(false);
    },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      own.onClick?.(event);
      setOpen(true);
    },
  });

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>{trigger}</PopoverPrimitive.Anchor>
      <span id={descriptionId} className="sr-only">
        {description}
      </span>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          aria-hidden="true"
          // Informational only: focus stays on the trigger both ways.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          className={cn(
            'z-50 max-w-[min(var(--spacing-popup-sm),var(--radix-popover-content-available-width))] rounded-md border border-border-strong bg-surface px-inset-sm py-1.5 text-caption text-fg shadow-overlay',
            className,
          )}
        >
          {content}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
